// The "0–1" section pills on the route card map, drawn on a canvas with the
// label baked in — the hike map's stage markers do the same (see
// hike_map/helpers/stageMarkerIcon.ts), because Mapbox draws a symbol layer's
// icons and its text in separate passes, so overlapping pills would show each
// other's numbers.

export const SECTION_PILL_PREFIX = 'route-card-section-';
export const PILL_PIXEL_RATIO = 2;

const HEIGHT = 18;
const PADDING_X = 6;
const FONT_PX = 11;
/** Average glyph width for the bold digits and dash, as a share of the font size. */
const GLYPH_WIDTH = 0.62;

export const PILL_COLOURS = {
  normal: { fill: '#17181D', text: '#FFFFFF', edge: '#FFFFFF' },
  selected: { fill: '#FDDF00', text: '#17181D', edge: '#17181D' },
} as const;

export function sectionPillIconId(label: string, selected: boolean): string {
  return `${SECTION_PILL_PREFIX}${label}${selected ? '-selected' : ''}`;
}

/** Pill size in logical pixels: wide enough for the label, never narrower than tall. */
export function sectionPillLayout(label: string): { width: number; height: number; fontPx: number } {
  const textWidth = Math.ceil(label.length * FONT_PX * GLYPH_WIDTH);
  return { width: Math.max(HEIGHT, textWidth + PADDING_X * 2), height: HEIGHT, fontPx: FONT_PX };
}

function drawPill(label: string, selected: boolean) {
  const { width, height, fontPx } = sectionPillLayout(label);
  const s = PILL_PIXEL_RATIO;
  const canvas = document.createElement('canvas');
  canvas.width = width * s;
  canvas.height = height * s;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const colours = selected ? PILL_COLOURS.selected : PILL_COLOURS.normal;
  const radius = (height * s) / 2;
  const edge = 1.5 * s;
  ctx.beginPath();
  ctx.moveTo(radius, edge / 2);
  ctx.lineTo(width * s - radius, edge / 2);
  ctx.arc(width * s - radius, radius, radius - edge / 2, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(radius, height * s - edge / 2);
  ctx.arc(radius, radius, radius - edge / 2, Math.PI / 2, (3 * Math.PI) / 2);
  ctx.closePath();
  ctx.fillStyle = colours.fill;
  ctx.fill();
  ctx.lineWidth = edge;
  ctx.strokeStyle = colours.edge;
  ctx.stroke();

  ctx.fillStyle = colours.text;
  ctx.font = `bold ${fontPx * s}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, (width * s) / 2, (height * s) / 2 + s / 2);

  const image = ctx.getImageData(0, 0, width * s, height * s);
  return { width: width * s, height: height * s, data: new Uint8Array(image.data.buffer) };
}

interface ImageHost {
  hasImage(id: string): boolean;
  addImage(id: string, image: { width: number; height: number; data: Uint8Array }, options: { pixelRatio: number }): void;
}

/** Registers both variants of each label's pill that the map doesn't have yet. */
export function ensureSectionPillIcons(map: ImageHost, labels: string[]): void {
  Array.from(new Set(labels)).forEach((label) => {
    [false, true].forEach((selected) => {
      const id = sectionPillIconId(label, selected);
      if (map.hasImage(id)) return;
      const image = drawPill(label, selected);
      if (image) map.addImage(id, image, { pixelRatio: PILL_PIXEL_RATIO });
    });
  });
}
